import React, { useEffect, useState } from 'react';
import NexusAIWebsite from './components/NexusAIWebsite';
import { Eye } from 'lucide-react';
import { ALL_ROUTES, ROUTE_META, APP_META } from './routeConfig';
import { CurrencyProvider } from './currency/CurrencyContext';

// Importar componentes directamente
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));
const CookiePolicy = React.lazy(() => import('./components/CookiePolicy'));
const DataDeletion = React.lazy(() => import('./components/DataDeletion'));
const OwnerPortal = React.lazy(() => import('./components/OwnerPortal'));
const ReviewForm = React.lazy(() => import('./components/ReviewForm'));

// Componente de banner de impersonación (mantener igual)
const ImpersonationBanner = () => {
  const [impersonationData, setImpersonationData] = useState(null);

  useEffect(() => {
    const isImpersonating = localStorage.getItem('impersonation-active') === 'true';
    const currentUser = JSON.parse(localStorage.getItem('nexusai-current-user') || '{}');
    const adminData = JSON.parse(localStorage.getItem('admin-backup') || '{}');
    const impersonationProduct = localStorage.getItem('nexusai-impersonation-product');
    
    setImpersonationData({
      isImpersonating,
      currentUser,
      adminData,
      impersonationProduct
    });
  }, []);

  const handleExitImpersonation = () => {
    if (impersonationData?.adminData?.token) {
      localStorage.setItem('nexusai-token', impersonationData.adminData.token);
      localStorage.setItem('nexusai-current-user', JSON.stringify(impersonationData.adminData.user));
      localStorage.removeItem('impersonation-active');
      localStorage.removeItem('admin-backup');
      localStorage.removeItem('nexusai-impersonation-product');
      
      window.location.href = '/admin';
    }
  };

  if (!impersonationData?.isImpersonating) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-100 border-b border-yellow-200 text-yellow-800 px-4 py-2 flex justify-between items-center">
      <div className="flex items-center">
        <Eye size={16} className="mr-2" />
        <span>
          Modo impersonación: Viendo como <strong>{impersonationData.currentUser.username}</strong>
          {impersonationData.impersonationProduct && (
            <span className="ml-1">| Producto: <strong>{impersonationData.impersonationProduct}</strong></span>
          )}
        </span>
      </div>
      <button 
        onClick={handleExitImpersonation}
        className="bg-yellow-200 hover:bg-yellow-300 text-yellow-800 px-3 py-1 rounded text-sm"
      >
        Salir
      </button>
    </div>
  );
};

function App() {
  const [currentRoute, setCurrentRoute] = useState('/');
  const [isLoading, setIsLoading] = useState(true);

  // 🆕 Lista de rutas válidas para validación
  const validRoutes = ['/', '/privacy', '/terms', '/cookies', '/data-deletion', '/resenas', ...Object.values(ALL_ROUTES)];

  // Función para normalizar rutas
  const normalizeRoute = (path) => {
    // Eliminar trailing slash excepto para la raíz
    if (path !== '/' && path.endsWith('/')) {
      return path.slice(0, -1);
    }
    return path;
  };

  // Función para validar si la ruta es válida
  const isValidRoute = (path) => {
    const normalized = normalizeRoute(path);
    if (validRoutes.includes(normalized)) return true;
    // Permitir rutas dinámicas del portal: /portal o /portal/XXXXXXXX
    if (normalized === '/portal' || normalized.match(/^\/portal\/[A-Za-z0-9]+$/)) return true;
    // Permitir tours públicos de NexusTour: /tour/XXXXXXXX
    if (normalized.match(/^\/tour\/[A-Za-z0-9]+$/)) return true;
    // Permitir artículos individuales del blog: /blog/mi-slug (deep-link indexable).
    if (normalized.match(/^\/blog\/[A-Za-z0-9-]+$/)) return true;
    // Permitir fichas de producto de Robotica: /producto/<id> (deep-link indexable).
    if (normalized.match(/^\/producto\/[a-z0-9-]+$/)) return true;
    return false;
  };

  // Función para actualizar URL
  const updateURL = (path) => {
    const normalizedPath = normalizeRoute(path);
    
    if (!isValidRoute(normalizedPath)) {
      console.warn(`Ruta no válida: ${path}, redirigiendo a /`);
      const newUrl = `${window.location.origin}/`;
      window.history.pushState({ path: '/' }, '', newUrl);
      setCurrentRoute('/');
      return;
    }
    
    const newUrl = `${window.location.origin}${normalizedPath}`;
    window.history.pushState({ path: normalizedPath }, '', newUrl);
    setCurrentRoute(normalizedPath);
  };

  // Función para navegar
  const handleNavigate = (path) => {
    updateURL(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    updateMetaTags(path);
  };

  // Función para actualizar meta tags
  const updateMetaTags = (path) => {
    const normalizedPath = normalizeRoute(path);
    
    const metadata = {
      '/': {
        title: 'NexusAI - Soluciones de Inteligencia Artificial',
        description: 'Transforma tu negocio con nuestras soluciones avanzadas de IA. Gestión de clientes, inventario inteligente, generación de documentos y más.',
        ogUrl: 'https://nexusai.company/',
        keywords: 'inteligencia artificial, IA, soluciones empresariales, automatización, gestión de clientes'
      },
      '/privacy': {
        title: 'Política de Privacidad - NexusAI',
        description: 'Conoce cómo NexusAI protege tu privacidad y maneja tus datos personales de forma segura y transparente.',
        ogUrl: 'https://nexusai.company/privacy',
        keywords: 'política de privacidad, protección de datos, GDPR, privacidad'
      },
      '/terms': {
        title: 'Términos de Servicio - NexusAI',
        description: 'Conoce los términos y condiciones de uso de los servicios de NexusAI.',
        ogUrl: 'https://nexusai.company/terms',
        keywords: 'términos de servicio, condiciones de uso, términos legales'
      },
      '/cookies': {
        title: 'Política de Cookies - NexusAI',
        description: 'Información sobre el uso de cookies en el sitio web de NexusAI y cómo gestionamos tu experiencia de navegación.',
        ogUrl: 'https://nexusai.company/cookies',
        keywords: 'política de cookies, cookies, navegación web'
      },
      '/data-deletion': {
        title: 'Eliminación de Datos - NexusAI',
        description: 'Solicita la eliminación de tus datos personales de los sistemas de NexusAI de forma fácil y segura.',
        ogUrl: 'https://nexusai.company/data-deletion',
        keywords: 'eliminación de datos, derecho al olvido, borrar datos personales'
      }
    };

    // Artículo individual del blog: meta base del blog (el título real por-post lo pone
    // Blog.jsx vía document.title al cargar el artículo — acá dejamos un fallback sano y con
    // og:url propio para que el link compartido apunte al artículo, no al blog genérico).
    const blogPostMatch = normalizedPath.match(/^\/blog\/[A-Za-z0-9-]+$/);
    // ROUTE_META → vistas públicas; APP_META → apps privadas (/app/*); metadata → legales
    const rawMeta = blogPostMatch
      ? { ...ROUTE_META['/blog'], ogUrl: `https://nexusai.company${normalizedPath}` }
      : (ROUTE_META[normalizedPath] || APP_META[normalizedPath] || metadata[normalizedPath] || metadata['/']);
    const meta = {
      description: '',
      keywords: '',
      ogUrl: `https://nexusai.company${normalizedPath}`,
      ...rawMeta,
    };
    
    // Actualizar título
    document.title = meta.title;
    
    // Actualizar meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', meta.description);
    
    // Actualizar meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', meta.keywords);
    
    // Actualizar Open Graph tags
    const ogTags = [
      { property: 'og:title', content: meta.title },
      { property: 'og:description', content: meta.description },
      { property: 'og:url', content: meta.ogUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'NexusAI' }
    ];
    
    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    
    // Actualizar Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: meta.title },
      { name: 'twitter:description', content: meta.description },
      { name: 'twitter:site', content: '@NexusAI_Company' }
    ];
    
    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Robots: las apps privadas (/app/*) no deben indexarse en buscadores
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    // La página de reseñas (/resenas) es oculta: no debe indexarse ni aparecer en buscadores.
    const noIndex = normalizedPath.startsWith('/app/') || normalizedPath === '/resenas';
    metaRobots.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
  };

  // 🆕 Manejar carga inicial y validación de ruta
  useEffect(() => {
    const currentPath = window.location.pathname;
    const normalizedPath = normalizeRoute(currentPath);
    
    console.log('🚀 App iniciando - Ruta actual:', currentPath);
    
    if (!isValidRoute(normalizedPath)) {
      console.warn(`❌ Ruta no válida: ${currentPath}, redirigiendo a /`);
      // Redirect a la página principal si la ruta no es válida
      window.history.replaceState({ path: '/' }, '', '/');
      setCurrentRoute('/');
      updateMetaTags('/');
    } else {
      console.log('✅ Ruta válida:', normalizedPath);
      setCurrentRoute(normalizedPath);
      updateMetaTags(normalizedPath);
    }
    
    setIsLoading(false);
  }, []);

  // Manejar botón atrás del navegador
  useEffect(() => {
    const handlePopState = (event) => {
      const path = window.location.pathname;
      const normalizedPath = normalizeRoute(path);
      
      console.log('🔙 Navegación hacia atrás:', path);
      
      if (!isValidRoute(normalizedPath)) {
        console.warn(`❌ Ruta no válida en popstate: ${path}, redirigiendo a /`);
        window.history.replaceState({ path: '/' }, '', '/');
        setCurrentRoute('/');
        updateMetaTags('/');
      } else {
        setCurrentRoute(normalizedPath);
        updateMetaTags(normalizedPath);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // 🆕 Manejar impersonación (mantener la lógica existente)
  useEffect(() => {
    if (isLoading) return; // Esperar a que termine la carga inicial
    
    const isImpersonating = localStorage.getItem('impersonation-active') === 'true';
    const impersonationProduct = localStorage.getItem('nexusai-impersonation-product');
    
    if (isImpersonating && impersonationProduct) {
      const currentPath = window.location.pathname;
      
      if (currentPath === '/' || currentPath === '/dashboard') {
        console.log("🎭 Detectada impersonación activa, redirigiendo a producto:", impersonationProduct);
        window.location.href = `/${impersonationProduct}`;
      }
    }
  }, [isLoading]);

  // 🆕 Mostrar loading durante la carga inicial
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-gray-700">Cargando NexusAI...</p>
        </div>
      </div>
    );
  }

  // Renderizado según ruta
  const renderPage = () => {
    const normalizedRoute = normalizeRoute(currentRoute);
    
    switch(normalizedRoute) {
      case '/privacy':
        return (
          <React.Suspense fallback={
            <div className="flex h-screen items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-lg font-medium text-gray-700">Cargando Política de Privacidad...</p>
              </div>
            </div>
          }>
            <PrivacyPolicy 
              currentUser={{}}
              onHome={() => handleNavigate('/')}
              onLogout={null}
              onNavigate={(view) => {
                switch(view) {
                  case 'terms': handleNavigate('/terms'); break;
                  case 'cookies': handleNavigate('/cookies'); break;
                  case 'data-deletion': handleNavigate('/data-deletion'); break;
                  default: handleNavigate('/');
                }
              }}
            />
          </React.Suspense>
        );
        
      case '/terms':
        return (
          <React.Suspense fallback={
            <div className="flex h-screen items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-lg font-medium text-gray-700">Cargando Términos de Servicio...</p>
              </div>
            </div>
          }>
            <TermsOfService 
              currentUser={{}}
              onHome={() => handleNavigate('/')}
              onLogout={null}
              onNavigate={(view) => {
                switch(view) {
                  case 'privacy': handleNavigate('/privacy'); break;
                  case 'cookies': handleNavigate('/cookies'); break;
                  case 'data-deletion': handleNavigate('/data-deletion'); break;
                  default: handleNavigate('/');
                }
              }}
            />
          </React.Suspense>
        );
        
      case '/cookies':
        return (
          <React.Suspense fallback={
            <div className="flex h-screen items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-lg font-medium text-gray-700">Cargando Política de Cookies...</p>
              </div>
            </div>
          }>
            <CookiePolicy 
              currentUser={{}}
              onHome={() => handleNavigate('/')}
              onLogout={null}
              onNavigate={(view) => {
                switch(view) {
                  case 'privacy': handleNavigate('/privacy'); break;
                  case 'terms': handleNavigate('/terms'); break;
                  case 'data-deletion': handleNavigate('/data-deletion'); break;
                  default: handleNavigate('/');
                }
              }}
            />
          </React.Suspense>
        );
        
      case '/data-deletion':
        return (
          <React.Suspense fallback={
            <div className="flex h-screen items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-lg font-medium text-gray-700">Cargando Eliminación de Datos...</p>
              </div>
            </div>
          }>
            <DataDeletion 
              currentUser={{}}
              onHome={() => handleNavigate('/')}
              onLogout={null}
              onNavigate={(view) => {
                switch(view) {
                  case 'privacy': handleNavigate('/privacy'); break;
                  case 'terms': handleNavigate('/terms'); break;
                  case 'cookies': handleNavigate('/cookies'); break;
                  default: handleNavigate('/');
                }
              }}
            />
          </React.Suspense>
        );
        
      case '/resenas':
        return (
          <React.Suspense fallback={
            <div className="flex h-screen items-center justify-center" style={{ background: '#312e81' }}>
              <div className="w-14 h-14 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          }>
            <ReviewForm onHome={() => handleNavigate('/')} />
          </React.Suspense>
        );

      default:
        // Portal público del propietario: /portal o /portal/:code
        if (normalizedRoute === '/portal' || normalizedRoute.startsWith('/portal/')) {
          return (
            <React.Suspense fallback={
              <div className="flex h-screen items-center justify-center bg-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-gray-700">Cargando Portal...</p>
                </div>
              </div>
            }>
              <OwnerPortal />
            </React.Suspense>
          );
        }
        return <NexusAIWebsite onNavigate={handleNavigate} />;
    }
  };

  return (
    <CurrencyProvider>
      <ImpersonationBanner />
      {renderPage()}
    </CurrencyProvider>
  );
}

export default App;